/*
  File: AgentGraph.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains the AgentGraph component, which displays a hierarchical graph of agents and their referrals.
  It utilizes the TreeNode component to render each node in the graph.

  Components:
  - TreeNode: Renders an individual node in the agent graph hierarchy.

  Props:
  - data: Array of Referral objects representing the agent graph data.

*/

import React from "react";
import { Referral } from "./types";
import "./AgentGraph.css"; // Import CSS file for styling

interface TreeNodeProps {
  node: Referral;
  depth: number;
  children?: React.ReactNode;
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, depth, children }) => {
  const levelColors: { [key: number]: string } = {};

  for (let level = 1; level <= 50; level++) {
    const hue = (level / 50) * 360;
    const saturation = 100;
    const lightness = 50;
    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    levelColors[level] = color;
  }

  const color = levelColors[depth] || "#000";

  return (
    <div className="tree-node" style={{ color: color }}>
      <div id={node.id} className="node-content">
        <div className="tree-node-text" title={depth.toString().toLowerCase()}>
          {node.first_name + " " + node.last_name}
        </div>
        {/* Depth indicator */}
      </div>
      <div className="children-container">{children}</div>
    </div>
  );
};

interface AgentGraphProps {
  data: Referral[] | undefined;
}

const AgentGraph: React.FC<AgentGraphProps> = ({ data }) => {
  const parentToChildren: { [key: string]: Referral[] } = {};

  data?.forEach((agent) => {
    if (agent.referred_by_id) {
      if (!parentToChildren[agent.referred_by_id]) {
        parentToChildren[agent.referred_by_id] = [];
      }
      parentToChildren[agent.referred_by_id].push(agent);
    }
  });

  const renderTree = (agent: Referral, depth: number): JSX.Element => {
    const children = parentToChildren[agent.id] || [];
    return (
      <TreeNode key={agent.id} node={agent} depth={depth}>
        {children.map((child) => renderTree(child, depth + 1))}
      </TreeNode>
    );
  };

  const roots = data?.filter((agent) => !agent.referred_by_id);

  return (
    <div className="tree">{roots?.map((root) => renderTree(root, 1))}</div>
  );
};

export default AgentGraph;
